/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, lazy, Suspense, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import { FlagGroup } from '@atlaskit/flag';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	deleteSessionItem,
	getSessionItem,
	setSessionItem,
} from '@atlassian/search-dialog/session-storage-utils';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onClickDisableDefinition } from '../analytics';
import { DefinitionUpdateConfirmationFlag } from '../curated/edit-definition-modal/definition-update-confirmation-flag';
import { EditDefinitionModal } from '../curated/edit-definition-modal/edit-definition-modal';
import { DefinitionEditHistory } from '../curated/edit-history-modal/edit-history-modal';
import { toggleDefinitionErrorPayload } from '../error-analytics';

import { FeedbackButtons } from './feedback-buttons';
import { FeedbackModal } from './feedback-modal';
import { FeedbackOptionsModal } from './feedback-options-modal';
import { AIDefinitionFooterSection, EditedDefinitionFooterSection } from './footer-section';
import { messages } from './messages';
import { useToggleDefinitionSupplier } from './useToggleDefinitionSupplier';

const styles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: token('space.050'),
		font: token('font.body'),
		color: token('color.text.accent.gray'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
});

const AkFeedbackFlag = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */ '@atlaskit/feedback-collector'
	).then(({ FeedbackFlag }) => {
		return {
			default: FeedbackFlag,
		};
	}),
);

const AI_DEFINITION_TOGGLE_CACHE_KEY = 'atlassian.search-ai.ai-definition-toggle';
interface DefinitionToggleCacheType {
	keyPhrase?: string;
}

export const DefinitionFooter: () => JSX.Element = () => {
	const {
		answerEditor,
		fireAnalyticsEvent,
		readingAidsOptions,
		isAnswered,
		hasError,
		commonAttributes,
		definitionToggleEnabled,
		sourceProduct,
	} = useAIAnswerContext();
	const { query: keyPhrase, workspaceId, cloudId } = commonAttributes;

	const { formatMessage, locale } = useIntl();

	const [showFeedbackOptionsModal, setShowFeedbackOptionsModal] = useState(false);
	const [showEditDefinitionModal, setShowEditDefinitionModal] = useState(false);

	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const [showEditHistoryModal, setShowEditHistoryModal] = useState(false);
	const [displayFlag, setDisplayFlag] = useState(false);
	const [displaySuccessFlag, setDisplaySuccessFlag] = useState(false);

	const cachedTermDefinitionDisabled = JSON.parse(
		getSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY) || '{}',
	) as DefinitionToggleCacheType;
	const [termDefinitionDisabled, setTermDefinitionDisabled] = useState(
		cachedTermDefinitionDisabled.keyPhrase === keyPhrase,
	);

	const { definitionCurationOptions } = readingAidsOptions || {};
	const editDefinitionEnabled = !!definitionCurationOptions;

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	const { toggleDefinition } = useToggleDefinitionSupplier();

	const onToggleDefinitionDisable = async () => {
		try {
			setTermDefinitionDisabled((current) => !current);
			await toggleDefinition({
				workspaceId,
				keyPhrase,
				isDisabled: !termDefinitionDisabled,
				cloudId,
				product: sourceProduct ?? '',
			});
			if (termDefinitionDisabled) {
				deleteSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY);
			} else {
				fireAnalyticsEvent(onClickDisableDefinition(commonAttributes));
				setSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY, JSON.stringify({ keyPhrase }));
			}
		} catch (error: unknown) {
			setTermDefinitionDisabled((current) => !current);
			fireAnalyticsEvent(toggleDefinitionErrorPayload(error, commonAttributes));
		}
	};

	return (
		<Fragment>
			{!hasError && isAnswered && !serveDefinitionUXUpdates && (
				<Flex
					justifyContent="space-between"
					xcss={styles.root}
					testId="definition-answer-card-footer"
				>
					<Box>
						<FeedbackButtons
							setShowFeedbackOptionsModal={setShowFeedbackOptionsModal}
							setDisplayFlag={setDisplayFlag}
						/>
					</Box>
					{definitionToggleEnabled && (
						<Checkbox
							isChecked={termDefinitionDisabled}
							label={messages.definition_footer_hide_term_definition_message.defaultMessage}
							value={messages.definition_footer_hide_term_definition_message.defaultMessage}
							onChange={onToggleDefinitionDisable}
						/>
					)}
				</Flex>
			)}
			{answerEditor ? (
				<EditedDefinitionFooterSection setShowEditHistoryModal={setShowEditHistoryModal} />
			) : (
				<AIDefinitionFooterSection />
			)}
			<ModalTransition>
				{showEditHistoryModal && editDefinitionEnabled && (
					<DefinitionEditHistory
						curatedDefinitionScopeConfig={definitionCurationOptions.curatedDefinitionScopeConfig}
						onClose={() => setShowEditHistoryModal(false)}
					/>
				)}
				{showFeedbackOptionsModal && (
					<FeedbackOptionsModal
						setShowFeedbackOptionsModal={setShowFeedbackOptionsModal}
						setShowEditDefinitionModal={setShowEditDefinitionModal}
						setShowFeedbackModal={setShowFeedbackModal}
						editDefinitionEnabled={editDefinitionEnabled}
					/>
				)}
				{showFeedbackModal && (
					<FeedbackModal
						setFeedbackModal={setShowFeedbackModal}
						isCuratedDefinition={!!answerEditor}
						setDisplayFlag={setDisplayFlag}
					/>
				)}
				{showEditDefinitionModal && editDefinitionEnabled && (
					<EditDefinitionModal
						curatedDefinitionScopeConfig={definitionCurationOptions.curatedDefinitionScopeConfig}
						closeEditModal={() => setShowEditDefinitionModal(false)}
						openFeedbackOptionsModal={() => setShowFeedbackOptionsModal(true)}
						onSaveEditModal={() => setDisplaySuccessFlag(true)}
					/>
				)}
			</ModalTransition>
			{displayFlag && (
				<FlagGroup onDismissed={() => setDisplayFlag(false)}>
					<Suspense fallback={<Fragment />}>
						<AkFeedbackFlag
							locale={locale}
							description={formatMessage(messages.definition_footer_feedback_flag_description)}
						/>
					</Suspense>
				</FlagGroup>
			)}
			{displaySuccessFlag && (
				<DefinitionUpdateConfirmationFlag onDismissed={() => setDisplaySuccessFlag(false)} />
			)}
		</Fragment>
	);
};
